/* eslint react/no-unused-state: 1 */
/* eslint no-undef: 1 */

import 'react-dates/initialize';
import React from 'react';
import { Logger } from 'aws-amplify';
import moment from 'moment';
import { DateRangePicker } from 'react-dates';
import {
  DropDown, PrimaryButton, MultiSelectDropDown, Pulse
} from 'wf-react-component-library';
import PurchaseOrderDetails from './PurchaseOrderDetails';
import { getCustomers, getVendors, getBuyers, getCategories, getUserByEmail } from '../services/api/P3ApiService';
import { getPurchaseOrders } from '../services/api/OCSApiService';
import { keyValueMapper } from '../utilities/key-value-util';
import { calculatePurchaseOrderTotals } from '../utilities/calculations';
import ErrorModal from './ErrorModal';
import 'react-dates/lib/css/_datepicker.css';
import './Search.scss';

import StickyWrapper from './StickyWrapper';

const logger = new Logger('Search.js');

class Search extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      selectedCustomers: null,
      dateOptions: {
        0: 'Load Date',
        1: 'Arrival Date',
      },
      startDate: null,
      endDate: null,
      dateType: null,
      isSearching: false,
      displayNoItemsFound: false,
      selectedVendor: null,
      selectedBuyer: null,
      selectedBuyer: null,
      errorMsg: ''
    }

    this.onSelect = this.onSelect.bind(this);
    this.onRemove = this.onRemove.bind(this);
    this.handleSearch = this.handleSearch.bind(this);
    this.handleReset = this.handleReset.bind(this);
    this.handleDropDownChange = this.handleDropDownChange.bind(this);
    this.resetPurchaseOrderDetails = this.resetPurchaseOrderDetails.bind(this);
    this.handleVendorChange = this.handleVendorChange.bind(this);
    this.handleBuyerChange = this.handleBuyerChange.bind(this);
  }

  componentDidMount = async() => {
    const { currentUser } = this.props;

    const userPromise = getUserByEmail(currentUser).then(userRecords => {
      const currentCategory = userRecords.map(user => user.categoryId).find(Boolean);
      logger.debug('current category', currentCategory);
      this.setState({ currentCategory });
    });

    const categoryPromise = getCategories().then(categories => {
      categories.forEach(cat => cat.name = cat.name.toLowerCase().split('/')[0]);
      const categoryDetails = keyValueMapper(categories, 'categoryId', 'name');
      logger.debug('category details', categoryDetails);
      this.setState({ categoryDetails });
    });
    await Promise.allSettled([userPromise, categoryPromise]);

    getCustomers().then((customerDetails) => {
      logger.debug('customers loaded', customerDetails);
      this.setState({ customerDetails });
    });

    getVendors().then((vendorDetails) => {
      logger.debug('vendors loaded', vendorDetails);
      const vendorOptions = this.mapVendorOptions(vendorDetails);
      this.setState({ vendorDetails, vendorOptions });
    });

    getBuyers(this.state.currentCategory).then((buyerDetails) => {
      logger.debug('buyers loaded', buyerDetails);
      const buyerOptions = this.mapBuyerOptions(buyerDetails);
      this.setState({ buyerDetails, buyerOptions });
    });
  }

  mapBuyerOptions = (buyerDetails) => {
    let buyerOptions = {};
    buyerDetails.forEach((buyer) => {
      const key = `id-${buyer.buyerId}`;
      buyerOptions[key] = buyer.description;
    });
    logger.debug('buyer options mapped ...', buyerOptions);
    return buyerOptions;
  }

  mapVendorOptions = (vendorDetails) => {
    let { currentCategory, categoryDetails } = this.state;
    if (currentCategory) {
      const currentCategoryName = categoryDetails[currentCategory];
      vendorDetails = vendorDetails.filter(vendor => vendor[currentCategoryName] === true );
    }
    vendorDetails.sort((a, b) => (a.code > b.code) ? 1 : -1)
    let vendorOptions = {};
    vendorDetails.forEach((vendor) => {
      const key = vendor.code;
      vendorOptions[key] = `${vendor.code} - ${vendor.description}`;
    });
    logger.debug('vendor options mapped ...', vendorOptions);
    return vendorOptions;
  }

  onSelect = (selectedList, selectedCustomer) => {
    this.resetPurchaseOrderDetails();
    this.setState({ selectedCustomers: selectedList });
  }

  onRemove = (selectedList, removedCustomer) => {
    this.resetPurchaseOrderDetails();
    this.setState({ selectedCustomers: selectedList });
  }

  handleSearch = async(buttonClick=false, selectedPurchaseOrders=[]) => {
    const { selectedBuyer, selectedVendor, selectedCustomers } = this.state;
    
    //Check for dateType and at least one date is selected
    const dateSelected = [this.state.startDate, this.state.endDate].some(val => val !== null);
    const typeSelected = ['0', '1'].includes(this.state.dateType);
    if (buttonClick && dateSelected && !typeSelected) {
      this.setState({ errorMsg: 'Please select a date for your search.' });
      return;
    }

    if (selectedBuyer === null || selectedBuyer === '') {
      this.setState({ errorMsg: 'Please select a buyer.' });
      return;
    }
    
    this.setState({ isSearching: true, displayNoItemsFound: false });
    const customerCodes = selectedCustomers === null ? '' : selectedCustomers.map(cust => cust.code);

    const params = {
      startDate: this.state.startDate === null ? null : this.state.startDate.format('YYYY-MM-DD'),
      endDate: this.state.endDate === null ? null : this.state.endDate.format('YYYY-MM-DD'),
      dateType: this.state.dateType,
      customerCode: customerCodes === '' ? customerCodes : customerCodes.join(','),
      vendorCode: selectedVendor === null ? '' : selectedVendor
    }

    const buyerId = selectedBuyer.split('-')[1];

    getPurchaseOrders(buyerId, params).then((purchaseOrderDetails) => {
      purchaseOrderDetails = purchaseOrderDetails.filter((po) => !po.submitted || po.submitted === 'true')
      logger.debug('POs', purchaseOrderDetails);
      calculatePurchaseOrderTotals(purchaseOrderDetails);
      // remove any purchase orders that were submitted as part of the bulk approval
      let filteredPurchaseOrders = purchaseOrderDetails;
      if (selectedPurchaseOrders.length > 0) filteredPurchaseOrders = purchaseOrderDetails.filter(po => !(selectedPurchaseOrders.includes(po.poId.toString())));
      this.setState({purchaseOrderDetails: filteredPurchaseOrders, isSearching: false, displayNoItemsFound: purchaseOrderDetails.length === 0 });
    }).catch((error) => {
      logger.error('Error Retrieving POs', error);
      this.setState({ displayNoItemsFound: true, isSearching: false })
    });
  }

  handleDropDownChange = (e) => {
    this.resetPurchaseOrderDetails();
    this.setState({ dateType: e.target.value });
  }

  handleVendorChange = (e) => {
    this.resetPurchaseOrderDetails();
    this.setState({ selectedVendor: e.target.value });
  }

  handleBuyerChange = (e) => {
    this.resetPurchaseOrderDetails();
    this.setState({ selectedBuyer: e.target.value });
  }

  resetPurchaseOrderDetails = () => {
    const { purchaseOrderDetails } = this.state;

    this.setState({
      isSearching: false, 
      displayNoItemsFound: false,
    });

    if (purchaseOrderDetails !== undefined && purchaseOrderDetails.length > 0) {
      this.setState({  
        purchaseOrderDetails: [],
      });
    }
  }

  handleReset = () => {
    this.setState({
      dateType: null,
      startDate: null,
      endDate: null,
      selectedCustomers: null,
      selectedVendor: null,
      selectedBuyer: null,
      purchaseOrderDetails: [],
      isSearching: false, 
      displayNoItemsFound: false,
    });
  }

  removeErrorMessages = () => this.setState({ errorMsg: '' });

  render() {
    const {
      customerDetails,
      dateOptions,
      selectedCustomers,
      purchaseOrderDetails,
      isSearching,
      displayNoItemsFound,
      dateType,
      vendorOptions,
      selectedVendor,
      vendorDetails,
      selectedBuyer,
      buyerOptions
    } = this.state;

    const { currentUser } = this.props;

    return (
      <div className="App">
        { this.state.errorMsg.length > 0
          ? <ErrorModal showErrorModal={this.state.errorMsg.length > 0} errorMessages={this.state.errorMsg} closeModal={this.removeErrorMessages} />
          : null
        }
        <StickyWrapper>
          <div className="search-criteria flex-container-row">
            
            <div className="flex-container-column" style={{ paddingBottom: '30px', maxWidth: '250px' }}>
              { vendorOptions === undefined
                ? <Pulse text="Loading Vendors..." />
                : <DropDown id="vendors" labelName="Vendor Name" options={vendorOptions} value={selectedVendor === null ? '' : selectedVendor} placeHolderText="Select a Vendor" handleChange={this.handleVendorChange} />
              }    
            </div>
            
            <div className="flex-container-column" style={{ paddingBottom: '30px' }}>
                { customerDetails === undefined
                  ? <Pulse text="Loading Customers..." />
                  : <MultiSelectDropDown selectedValues={selectedCustomers} placeHolderText="Select a Customer(s)" labelName="Ship to Customer(s)" options={customerDetails} displayValue="description" id="customers" onSelect={this.onSelect} onRemove={this.onRemove} />
                }  
              </div>

            <div className="flex-container-column" style={{ paddingBottom: '30px' }}>
              <DropDown id="dateOptions" labelName="Date" options={dateOptions} value={dateType === null ? '' : dateType} placeHolderText="Select a Date Option" handleChange={this.handleDropDownChange} />
            </div>

            <div className="flex-container-column" style={{ paddingLeft: '20px', paddingRight: '20px', paddingBottom: '30px' }}>
              <div className="wf-form-element-label">
                From:
                <span style={{ paddingLeft: '110px' }}>To:</span>
              </div>
              <DateRangePicker
                startDate={this.state.startDate} // momentPropTypes.momentObj or null,
                startDateId="start-dt" // PropTypes.string.isRequired,
                endDate={this.state.endDate} // momentPropTypes.momentObj or null,
                endDateId="end-dt" // PropTypes.string.isRequired,
                onDatesChange={({ startDate, endDate }) => {
                  this.resetPurchaseOrderDetails();
                  this.setState({ startDate, endDate })}
                }
                focusedInput={this.state.focusedInput}
                onFocusChange={(focusedInput) => this.setState({ focusedInput })}
                isOutsideRange={(day) => day.isBefore(moment().subtract(3, 'month'))}
                minimumNights={0}
                readOnly={true}
              />
            </div>

            <div className="flex-container-column" style={{ paddingBottom: '30px' }}>
              { buyerOptions === undefined
                ? <Pulse text="Loading Buyers..." />
                : <DropDown id="buyers" labelName="Buyer" options={buyerOptions} placeHolderText="Select a Buyer" value={(selectedBuyer === null || selectedBuyer === '') ? '' : selectedBuyer} handleChange={this.handleBuyerChange} />
              }
            </div>

            <div className="flex-container-column">
              <div className="flex-container-row search-buttons">
                <PrimaryButton onClick={() => this.handleSearch(true)} name="Search" />
                <PrimaryButton onClick={this.handleReset} name="Reset" />
              </div>
            </div>
          </div>

          <div className="flex-container-row" style={{ textAlign: 'center', paddingBottom: "10px" }}>
              <div className="flex-container-column xpo-count">
                {purchaseOrderDetails === undefined || purchaseOrderDetails.length === 0 || isSearching === true
                  ? '' 
                  : `${purchaseOrderDetails.length} XPOs Returned`
                }
              </div>
          </div>
        </StickyWrapper>

        { isSearching
          ? <div className="flex-container-row"><Pulse text="Loading Search Criteria..." /></div>
          : null
        }

        { displayNoItemsFound
          ? <div className="flex-container-row">
              <div className="pulse-container headline-small">No POs Found</div>
            </div>
          : null
        }

        { purchaseOrderDetails === undefined || purchaseOrderDetails.length === 0 || isSearching === true
          ? null
          : <PurchaseOrderDetails handleSearch={this.handleSearch} vendorDetails={vendorDetails} searchId={Math.floor(1000000 * Math.random()) * 100000000} currentUser={currentUser} purchaseOrderDetails={purchaseOrderDetails} customerDetails={customerDetails}  />
        }
      </div>
    );
  }
}

export default Search;
